import React, { useCallback, useEffect, useState } from 'react';
import {
  ClipboardCheck, X, CheckCircle2, XCircle, PenLine, RefreshCw, AlertTriangle, Info, Umbrella,
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { STATUS_STYLES } from '../components/LeaveSection';

const TABS = [
  { key: 'pending', label: 'Do zatwierdzenia' },
  { key: 'supervisor', label: 'Czekają na przełożonego' },
  { key: 'approved', label: 'Zatwierdzone' },
  { key: 'rejected', label: 'Odrzucone / anulowane' },
  { key: 'errors', label: 'Błędy zapisu' },
];

const formatDate = (s) => (s ? new Date(s + 'T00:00:00').toLocaleDateString('pl-PL') : '');
const formatDateTime = (s) => (s ? new Date(s).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '');
const fmtDays = (d) => (d == null ? '–' : `${Math.round(d * 100) / 100} dni`);

/** Kadry → Wnioski urlopowe: zatwierdzanie wniosków złożonych w CRM. */
const LeaveRequestsAdmin = () => {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [writeEnabled, setWriteEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/kadry/urlopy', { params: { group: tab } });
      setItems(res.data.items || []);
      setWriteEnabled(!!res.data.writeEnabled);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Nie udało się pobrać wniosków');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setComment('');
    try {
      const res = await api.get(`/kadry/urlopy/${id}`);
      setDetail(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Nie udało się pobrać wniosku');
    } finally {
      setDetailLoading(false);
    }
  };

  const act = async (action, successMsg) => {
    if (!detail) return;
    setBusy(true);
    try {
      const res = await api.post(`/kadry/urlopy/${detail.request.id}/${action}`, { comment: comment || null });
      const st = res.data?.status;
      if (st === 'BLAD_ZAPISU') {
        toast.error(`Zapis do KS-ZZL nieudany: ${res.data.errorMessage || ''}`);
      } else {
        toast.success(successMsg);
      }
      setDetail(null);
      load();
    } catch (err) {
      const data = err.response?.data;
      const details = data?.details?.length ? ` ${data.details.join(' ')}` : '';
      toast.error((data?.error || 'Operacja nieudana.') + details);
    } finally {
      setBusy(false);
    }
  };

  const r = detail?.request;
  const v = detail?.validation;
  const canDecide = r && (r.status === 'ZLOZONY' || r.status === 'BLAD_ZAPISU');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center shadow-md">
          <ClipboardCheck className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">Wnioski urlopowe</h1>
          <p className="text-sm text-gray-500">Zatwierdzanie wniosków złożonych w CRM</p>
        </div>
      </div>

      {!writeEnabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex items-start gap-3 text-sm text-blue-900">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <span>
            Etap 1: po zatwierdzeniu wpisz urlop w KS-ZZL jak dotychczas, a potem kliknij
            <b> „Wpisane ręcznie”</b>. Bilans i kontrola nakładania pochodzą z KS-ZZL na bieżąco.
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              tab === t.key ? 'bg-primary-600 text-white border-primary-600 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-40"><LoadingSpinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">Brak wniosków w tej zakładce.</div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Pracownik', 'Rodzaj', 'Termin', 'Dni rob.', 'Złożono', 'Status'].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-[11px] font-semibold tracking-wider text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((it) => (
                <tr key={it.id} onClick={() => openDetail(it.id)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-3 text-sm text-gray-900">{it.userName}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{it.kindName}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(it.dateFrom)} – {formatDate(it.dateTo)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{it.daysEstimate ?? '–'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(it.submittedAt)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[it.status] || 'bg-gray-100 text-gray-700'}`}>
                      {it.statusLabel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50" onClick={() => !busy && setDetail(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl p-6">
              <button onClick={() => setDetail(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" aria-label="Zamknij">
                <X className="w-5 h-5" />
              </button>
              {detailLoading || !r ? (
                <div className="flex justify-center items-center h-40"><LoadingSpinner size="lg" /></div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{r.userName}</h2>
                    <p className="text-sm text-gray-600">
                      {r.kindName}: <b>{formatDate(r.dateFrom)} – {formatDate(r.dateTo)}</b>
                      {r.daysEstimate != null && <> · {r.daysEstimate} dni rob.</>}
                    </p>
                    {r.comment && <p className="text-sm text-gray-500 mt-1">Komentarz pracownika: {r.comment}</p>}
                    <span className={`inline-flex mt-2 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[r.status] || ''}`}>{r.statusLabel}</span>
                    {r.waitingForName && (
                      <p className="text-xs text-violet-700 mt-1">Czeka na podpis: {r.waitingForName}</p>
                    )}
                    {r.supervisorSignedByName && (
                      <p className="text-xs text-gray-600 mt-1">
                        Podpisał przełożony: <b>{r.supervisorSignedByName}</b>
                        {r.supervisorOnBehalfOfName ? ` (w zastępstwie ${r.supervisorOnBehalfOfName})` : ''}, {formatDateTime(r.supervisorSignedAt)}
                        {r.supervisorComment ? ` – ${r.supervisorComment}` : ''}
                      </p>
                    )}
                    {r.decidedByName && (
                      <p className="text-xs text-gray-500 mt-1">
                        Decyzja: {r.decidedByName}, {formatDateTime(r.decidedAt)}{r.decisionComment ? ` – ${r.decisionComment}` : ''}
                      </p>
                    )}
                    {r.errorMessage && <p className="text-sm text-red-700 mt-2">{r.errorMessage}</p>}
                  </div>

                  {detail.balance?.contracts?.length > 0 && (
                    <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 flex items-start gap-3">
                      <Umbrella className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-gray-700">
                        {detail.balance.contracts.map((c) => (
                          <div key={c.idUmp}>
                            Bilans w KS-ZZL: pozostało <b>{fmtDays(c.pozostalyDni)}</b> z należnych {fmtDays(c.naleznyDni)}
                            {' '}(wykorzystano {fmtDays(c.wykorzystanyDni)})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {v && (
                    <div className="space-y-2">
                      {v.ok ? (
                        <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-800 border border-emerald-200">
                          <CheckCircle2 className="w-4 h-4 mt-0.5" /> Kontrola KS-ZZL: bez zastrzeżeń.
                        </div>
                      ) : (
                        (v.errors || []).map((e, i) => (
                          <div key={i} className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-red-50 text-red-800 border border-red-200">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{e}</span>
                          </div>
                        ))
                      )}
                      {(v.overlaps || []).map((o, i) => (
                        <div key={`o${i}`} className="text-xs text-red-700 pl-6">
                          {o.source}: {o.typeName} {formatDate(o.dateFrom)} – {formatDate(o.dateTo)}
                        </div>
                      ))}
                    </div>
                  )}

                  {canDecide && (
                    <div>
                      <label htmlFor="decisionComment" className="block text-sm text-gray-700 mb-1.5">
                        Komentarz do decyzji <span className="text-gray-400 text-xs">(opcjonalny, widzi go pracownik)</span>
                      </label>
                      <textarea
                        id="decisionComment"
                        rows={2}
                        maxLength={500}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  )}

                  <div className="flex flex-wrap justify-end gap-2 pt-1">
                    {canDecide && (
                      <>
                        <button disabled={busy} onClick={() => act('reject', 'Wniosek odrzucony')} className="btn btn-secondary flex items-center gap-2">
                          <XCircle className="w-4 h-4" /> Odrzuć
                        </button>
                        <button
                          disabled={busy || (v && !v.ok)}
                          onClick={() => act('approve', writeEnabled ? 'Zatwierdzono i wpisano do KS-ZZL' : 'Zatwierdzono - wpisz urlop w KS-ZZL')}
                          className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {busy ? <LoadingSpinner size="sm" /> : <CheckCircle2 className="w-4 h-4" />}
                          {writeEnabled ? 'Zatwierdź i wpisz do KS-ZZL' : 'Zatwierdź'}
                        </button>
                      </>
                    )}
                    {r.status === 'ZATWIERDZONY_DO_WPISU' && (
                      <button disabled={busy} onClick={() => act('mark-manual', 'Oznaczono jako wpisane w KS-ZZL')} className="btn btn-primary flex items-center gap-2">
                        <PenLine className="w-4 h-4" /> Wpisane ręcznie w KS-ZZL
                      </button>
                    )}
                    {r.status === 'ZAPISANY_BEZ_PRZELICZENIA' && (
                      <button disabled={busy} onClick={() => act('retry-refresh', 'Zlecono przeliczenie bilansu')} className="btn btn-primary flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" /> Zleć przeliczenie ponownie
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveRequestsAdmin;
